import React from "react";
import _ from "lodash";
import dateformat from "dateformat";
import InfiniteScroll from "src/components/infiniteScroll";
import { useLeadJourneyInfiniteQuery } from "src/query/experiences/leadsAndReports/leads";
import { convertDatesToTimezone, dateKey } from "src/services/experiences/leadsAndReports/dateUtils";
import { EmptyState } from "../../../common";
import { TableCellText } from "../../../common/TableCellText";
import { ReportDateFilter } from "src/types/experiences/leadsAndReports";
import styles from "../leads.module.scss";

interface LeadJourneyProps {
  storyboardId: string;
  profileId: string;
  dateFilter: ReportDateFilter | null;
}

export const LeadJourney: React.FC<LeadJourneyProps> = ({ storyboardId, profileId, dateFilter }) => {
  const request = { storyboard: storyboardId, ...convertDatesToTimezone(dateFilter ?? undefined) };
  const { data, hasNextPage, isFetchingNextPage, isLoading, loadMore } = useLeadJourneyInfiniteQuery(
    profileId,
    request,
    dateKey(dateFilter ?? undefined),
    20,
    Boolean(dateFilter),
  );

  const items: any[] = (data?.pages ?? []).flatMap((p: any) => p.records ?? []);

  const containerRef = React.useRef<HTMLDivElement>(null);
  const [atEnd, setAtEnd] = React.useState(false);

  const getScrollEl = React.useCallback(
    () => containerRef.current?.querySelector<HTMLDivElement>("#infinite-scroll") ?? null,
    [],
  );

  const updateAtEnd = React.useCallback(() => {
    const el = getScrollEl();
    if (!el) return;
    setAtEnd(el.scrollHeight - el.scrollTop - el.clientHeight <= 1);
  }, [getScrollEl]);

  React.useEffect(() => {
    const el = getScrollEl();
    if (!el) return;
    el.addEventListener("scroll", updateAtEnd);
    return () => el.removeEventListener("scroll", updateAtEnd);
  }, [getScrollEl, updateAtEnd]);

  React.useEffect(() => {
    updateAtEnd();
  }, [items.length, updateAtEnd]);

  return (
    <section className={styles.leadJourney}>
      <hr className={styles.divider} />
      <h6 className={styles.sectionHeading}>Lead Journey</h6>
      <div ref={containerRef}>
        <InfiniteScroll
          className={`${styles.journeyContainer} ${atEnd ? styles.atEnd : ""}`}
          loading={isFetchingNextPage || isLoading}
          hasMore={hasNextPage}
          loadMore={loadMore}
        >
          {items.length === 0 && !isFetchingNextPage && !isLoading && (
            <EmptyState message="No journey yet." />
          )}
     
          {items.map((interaction, index) => (
            <div key={index} className={styles.journeyDetails}>
              <div className={styles.journeyTimeline}>
                <span>{dateformat(interaction.eventDate, "dd mmm yyyy")}</span>
              </div>
              <div className={styles.journeyInteraction}>
                <span className={styles.type}>{_.get(interaction, "type", "")}</span>
                <TableCellText
                  className={styles.name}
                  text={_.get(interaction, "name", "")}
                  wrap
                />
                {_.get(interaction, "campaign") && (
                  <TableCellText
                    className={styles.campaign}
                    text={`${_.get(interaction, "campaign.source", "")}/${_.get(interaction, "campaign.medium", "")}`}
                  />
                )}
              </div>
            </div>
          ))}
        </InfiniteScroll>
      </div>
    </section>
  );
};
